/*
 * @Author: Claude
 * @Date: 2025-11-08
 * @Description: 获取音频/视频文件时长
 */

import { t } from '@/utils/helper/intl';

import { isAudioFile, isMediaFile } from './fileTypeDetector';

/**
 * 获取媒体文件时长
 * @param file - 音频或视频文件
 * @returns Promise<number> - 返回时长（单位：秒）
 */
export const getMediaDuration = (file: File): Promise<number> => {
  return new Promise((resolve, reject) => {
    // 检查文件类型（通过扩展名和 MIME type）
    if (!isMediaFile(file)) {
      reject(new Error('文件类型必须是音频或视频'));
      return;
    }

    const url = URL.createObjectURL(file);
    const mediaElement = document.createElement(
      isAudioFile(file) ? 'audio' : 'video',
    ) as HTMLMediaElement;

    mediaElement.onloadedmetadata = () => {
      const duration = mediaElement.duration;
      URL.revokeObjectURL(url);

      if (isNaN(duration) || duration === Infinity) {
        reject(new Error('无法读取媒体时长'));
      } else {
        resolve(duration);
      }
    };

    mediaElement.onerror = () => {
      URL.revokeObjectURL(url);
      reject(new Error('无法加载媒体文件'));
    };

    mediaElement.src = url;
  });
};

/**
 * 格式化时长为可读字符串
 * @param seconds - 秒数
 * @returns 格式化的时长字符串，如 "1分30秒" 或 "45秒"
 */
export const formatDuration = (seconds: number): string => {
  const minutes = Math.floor(seconds / 60);
  const secs = Math.floor(seconds % 60);

  if (minutes > 0) {
    return `${minutes}${t('merror.common.minute')}${secs}${t('merror.common.second')}`;
  }
  return `${secs}${t('merror.common.second')}`;
};
